// Date-range resolution for the Nutrition Progress range controls.
// Pure functions over plans + a reference "today"; returns native Date objects
// to stay compatible with the legacy dateFrom/dateTo state in Nutrition.js.

import moment from "moment";

// Reuse the existing range-overlap predicate instead of re-deriving it.
import { isPlanInRange } from "../nutrition-helpers";
import { DEFAULT_QUICK_RANGE, QUICK_RANGES } from "./constants";

// A YYYYMMDD number (the format every plan / daily-stat uses) from any date.
export const toUtcNumber = (date) => Number(moment(date).format("YYYYMMDD"));

// A native Date from a YYYYMMDD number.
export const utcToDate = (utc) => moment(String(utc)).toDate();

// Trailing window ending today for a quick-select id (2W/1M/3M/6M/lifetime).
// Falls back to the default range for an unknown id.
// "lifetime" resolves to a 10-year window; the hook overrides it to the
// earliest plan start date when plans are available.
// `ranges`/`defaultId` default to the main filter's own QUICK_RANGES/
// DEFAULT_QUICK_RANGE, so both existing call sites (useNutritionProgressFilters.js)
// are unaffected — passing a different set (e.g. BODYWEIGHT_QUICK_RANGES,
// BodyweightTrendCard.js) reuses this exact same date math for an
// independent quick-select elsewhere, instead of a second copy of it.
export const resolveQuickRange = (
  quickId,
  today = new Date(),
  ranges = QUICK_RANGES,
  defaultId = DEFAULT_QUICK_RANGE
) => {
  const def =
    ranges.find((q) => q.id === quickId) ||
    ranges.find((q) => q.id === defaultId);
  const to = moment(today).endOf("day");
  const from = moment(today).subtract(def.amount, def.unit).startOf("day");
  return { dateFrom: from.toDate(), dateTo: to.toDate() };
};

// The date span a nutrition plan covers, or null when there is no plan.
export const rangeFromPlan = (plan) => {
  if (!plan) return null;
  return {
    dateFrom: utcToDate(plan.utcStartDate),
    dateTo: utcToDate(plan.utcEndDate),
  };
};

// The plan whose span covers a given YYYYMMDD day, or null.
export const planForUtcDate = (nutritionPlans, utcDate) =>
  (nutritionPlans || []).find(
    (p) => p.utcStartDate <= utcDate && p.utcEndDate >= utcDate
  ) || null;

// The plan with the latest end date (used for the "due for renewal" state).
export const mostRecentPlan = (nutritionPlans = []) =>
  nutritionPlans.reduce(
    (latest, p) => (!latest || p.utcEndDate > latest.utcEndDate ? p : latest),
    null
  );

// Every plan overlapping a YYYYMMDD range — the multi-plan case for a period.
export const plansInRange = (nutritionPlans, fromUtc, toUtc) =>
  (nutritionPlans || []).filter((p) => isPlanInRange(p, fromUtc, toUtc));

// Phase spans for the Calendar periodization band and the Summary chart's
// phase markers - `track` selects which axis of the dual-track model to read
// (defaults to training, the only track every existing call site used before
// nutritionPhases existed).
// The annual planner stores phases as a catalog (`phases:[{phaseName,phaseColor}]`)
// plus dated anchors (`datesAttachments:[{phaseName,dateAttachment}]`) — one
// anchor per WEEK, toggled independently per week in the planner's own UI
// (PhaseCheckbox.js/usePhaseSelection.js): a week with no checked phase is a
// real "no phase assigned" state, not a gap to bridge. This mirrors exactly
// how the planner's own ListView.js reads the same data: a strict per-week
// lookup, with no forward-fill across an unmarked week.
export const buildPhaseSpans = (
  athlete,
  fromUtc,
  toUtc,
  track = "trainingPhases"
) => {
  // `athlete.annualPlan` is already normalized server-side (see
  // ensure-coach.js), so `tp` is already the exact legacy shape the rest of
  // this function expects - no local transform needed.
  const tp = athlete?.annualPlan?.[track];
  const anchors = tp?.datesAttachments;
  if (!Array.isArray(anchors) || !anchors.length) return [];

  const colorOf = (phaseName) =>
    (tp.phases || []).find((p) => p.phaseName === phaseName)?.phaseColor ||
    null;

  const sorted = [...anchors]
    .filter((a) => a?.dateAttachment != null)
    .sort((a, b) => a.dateAttachment - b.dateAttachment);

  // Each anchor's own week is capped by the very NEXT anchor's date —
  // whatever its name — so two anchors landing less than 7 days apart (a
  // real data-quality wrinkle seen in production planner data) never
  // produce overlapping ranges. Left uncapped (a full natural week) when
  // nothing follows soon, rather than stretched to bridge a real gap.
  const weeks = sorted.map((anchor, i) => {
    const startUtc = Number(anchor.dateAttachment);
    const naturalEndUtc = Number(
      moment(String(startUtc), "YYYYMMDD").add(6, "days").format("YYYYMMDD")
    );
    const next = sorted[i + 1];
    const endUtc = next
      ? Math.min(
          naturalEndUtc,
          Number(
            moment(String(next.dateAttachment), "YYYYMMDD")
              .subtract(1, "day")
              .format("YYYYMMDD")
          )
        )
      : naturalEndUtc;
    return { name: anchor.phaseName, startUtc, endUtc };
  });

  // Merge consecutive weeks of the same phase into one span. Weeks are only
  // continuous when their (now non-overlapping) ranges are perfectly
  // back-to-back — i.e. no unmarked week sits between them.
  const runs = [];
  for (const week of weeks) {
    const last = runs.at(-1);
    const isContiguous =
      last &&
      last.name === week.name &&
      Number(
        moment(String(last.endUtc), "YYYYMMDD").add(1, "day").format("YYYYMMDD")
      ) === week.startUtc;
    if (isContiguous) {
      last.endUtc = week.endUtc;
    } else {
      runs.push({ ...week });
    }
  }

  // Keep only spans that overlap the visible window, clipped to it.
  // isOngoing is read off the PRE-clip startUtc - once startUtc itself is
  // clamped below, a phase that started earlier and one that happens to
  // start exactly on fromUtc both land on the same clipped value, so this
  // is the only place that distinction still exists.
  return runs
    .filter((run) => run.startUtc <= toUtc && run.endUtc >= fromUtc)
    .map((run) => ({
      name: run.name,
      color: colorOf(run.name),
      isOngoing: run.startUtc < fromUtc,
      startUtc: Math.max(run.startUtc, fromUtc),
      endUtc: Math.min(run.endUtc, toUtc),
    }));
};

// The single phase active on one specific day (typically today), for status
// summaries that show "current phase" rather than a calendar band. Reuses
// buildPhaseSpans with a single-day window - every span it returns already
// satisfies startUtc <= todayUtc <= endUtc by construction, and a given day
// belongs to at most one phase, so the first result is the answer.
export const currentTrainingPhase = (athlete, todayUtc) => {
  const [span] = buildPhaseSpans(athlete, todayUtc, todayUtc, "trainingPhases");
  return span ? { name: span.name, color: span.color } : null;
};

// Same as currentTrainingPhase, for the nutrition track. Still reads as null
// for every athlete today - not because writes are blocked (they aren't
// anymore), but because there is no UI yet to assign a nutrition phase to a
// specific week (the checkbox grid is training-only, see
// usePhaseSelection.js).
export const currentNutritionPhase = (athlete, todayUtc) => {
  const [span] = buildPhaseSpans(
    athlete,
    todayUtc,
    todayUtc,
    "nutritionPhases"
  );
  return span ? { name: span.name, color: span.color } : null;
};
